import { api_url } from "./config/config";
import { AuthService } from "./services/auth.service";
import { ConfigService } from "./config/config.service";
import ErrorHandler from "./services/ErrorHandler";
import { LRUCache } from 'lru-cache';

export interface ApiResponse<T = unknown> {
  status: string,
  message: string,
  errors: T,
  data: T;
}

export interface ApiClient {
  get<T>(path: string, query?: Record<string, unknown>): Promise<ApiResponse<T>>;
  post<T>(path: string, body: unknown): Promise<ApiResponse<T>>;
}

interface RetryConfig {
  maxRetries: number;                          // Maximum number of retries
  retryDelays: number[];                       // Delay in milliseconds between retries
  retryableStatusCodes: number[];              // HTTP status codes that should trigger a retry
  retryOnNetworkError: boolean;                // Whether to retry on network errors
}

const defaultRetryConfig: RetryConfig = {
  maxRetries: 3,
  retryDelays: [1000, 2000, 4000],             // Example for exponential backoff
  retryableStatusCodes: [429, 500, 502, 503, 504],
  retryOnNetworkError: true
};

/**
 * Represents the API service with methods to perform HTTP requests.
 * This service handles caching for GET requests to reduce network load and improve response times.
 * It provides methods to perform GET and POST operations, utilizing modern JavaScript Fetch API.
 * 
 * Usage:
 * - Use `get` for retrieving data, which will be cached based on URL and query parameters.
 * - Use `post` for submitting data, which are not cached.
 */
export default class Api {
  private api_url: string;
  private headers = new Headers({ 'Content-Type': 'application/json' })
  private cache: LRUCache<string, ApiResponse<unknown>>;
  private retryConfig: RetryConfig;

  /**
   * Constructs the Api class with dependencies for authentication
   * and configuration services.
   * 
   * @param authService The authentication service used for retrieving API tokens.
   * @param configService The configuration service used to determine environment-specific settings.
   */
  constructor(
    private authService: AuthService,
    private configService: ConfigService,
    maxCacheSize: number = 500,
    retryConfig?: Partial<RetryConfig>
  ) {
    this.api_url = api_url;
    this.cache = new LRUCache({
      max: maxCacheSize,
      // ttl: 1000 * 60 * 5 // TTL in milliseconds (5 minutes)
      ttl: 0

    });
    this.retryConfig = { ...defaultRetryConfig, ...retryConfig };
  }

  /**
   * Initializes the API client by setting up necessary headers
   * and ensures the API token is prepared if not in a test environment.
   * Throws an error if the API token is required but not obtainable
   * outside of development environments.
   */
  async init() {
    if (!this.configService.isTestEnvironment()) {
      const apiToken = await this.authService.getToken();

      // 一時的にコメントアウト
      // if (!apiToken && !this.configService.isDevelopmentEnvironment()) {
      //   throw ErrorHandler.logError(new Error("API token is required for non-development environments"));
      // }
      if (apiToken) {
        this.headers.append('Authorization', `Bearer ${apiToken}`);
      }
    }
  }

  /**
     * Performs an HTTP request using the fetch API. Caches responses for GET requests to optimize performance.
     * @param method The HTTP method ('GET' or 'POST').
     * @param path The API endpoint path.
     * @param body The payload for POST requests, undefined for GET.
     * @param query Optional query parameters for the request.
     * @returns A promise that resolves with the ApiResponse<T> type, either from cache or a fresh network request.
     * @throws Will throw an error if the HTTP request fails or if the network is unreachable.
     */
  private async request<T>(method: string, path: string, body?: unknown, query?: Record<string, unknown>): Promise<ApiResponse<T>> {

    const url = new URL(this.api_url + path);

    if (query) {
      url.search = new URLSearchParams(query as any).toString();
    }
    const cacheKey = `${method}:${url.toString()}`;

    // if (method === 'GET' && this.cache.has(cacheKey)) {
    //   const cachedResponse = this.cache.get(cacheKey);
    //   if (cachedResponse) {
    //     return cachedResponse as ApiResponse<T>;
    //   }
    // }

    const options: RequestInit = { method, headers: this.headers };
    if (body) {
      options.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(url.toString(), options);
      const data = await response.json();
      if (!response.ok) {
        throw ErrorHandler.logError(new Error(`API request failed with status ${response.status}: ${response.statusText}`));
      }
      if (method === 'GET') {
        this.cache.set(cacheKey, data);
      }
      return data;
    } catch (error) {
      ErrorHandler.logError(new Error(`API request error: ${error}`));
      throw error;
    }
  }

  /**
   * Executes an HTTP request with retry logic.
   * This method wraps the basic request method to add retries with exponential backoff for
   * certain types of failures, such as server errors or network issues.
   *
   * @param {string} method - The HTTP method to be used (e.g., 'GET', 'POST').
   * @param {string} path - The endpoint path for the request.
   * @param {unknown} [body] - Optional payload for POST requests.
   * @param {Record<string, unknown>} [query] - Optional query parameters for the request.
   * @returns {Promise<ApiResponse<T>>} - A promise that resolves with the response data in the format specified by ApiResponse.
   * @template T - The expected type of the response data.
   * @throws {Error} - Throws an error if all retry attempts fail or if a non-retryable error occurs.
   */
  private async requestWithRetry<T>(method: string, path: string, body?: unknown, query?: Record<string, unknown>): Promise<ApiResponse<T>> {
    let attempt = 0;

    while (true) {
      try {
        return await this.request<T>(method, path, body, query);
      } catch (error) {
        if (!this.shouldRetry(error, attempt)) {
          throw error;
        }
        if (attempt < this.retryConfig.retryDelays.length) {
          await this.delay(this.retryConfig.retryDelays[attempt]);
        } else {
          await this.delay(this.retryConfig.retryDelays[this.retryConfig.retryDelays.length - 1]); // Use the last specified delay
        }
        attempt++;
        ErrorHandler.logError(new Error(`Retrying request to ${path}: attempt ${attempt}`));
      }
    }
  }


  /**
   * Determines whether a request should be retried based on the error received and the number of attempts already made.
   *
   * @param {any} error - The error returned from the failed request.
   * @param {number} attempt - The current retry attempt count.
   * @param {number} maxRetries - The maximum number of retries allowed.
   * @returns {boolean} - True if the request should be retried, false otherwise.
   */
  private shouldRetry(error: any, attempt: number): boolean {
    const statusCode = error.status || 0;
    return attempt < this.retryConfig.maxRetries &&
      (this.retryConfig.retryableStatusCodes.includes(statusCode) ||
        (error.name === 'NetworkError' && this.retryConfig.retryOnNetworkError));
  }

  /**
   * Delays the execution for a specified number of milliseconds, typically used to space out retry attempts.
   *
   * @param {number} ms - The number of milliseconds to delay.
   * @returns {Promise<void>} - A promise that resolves after the specified delay, used to pause execution within async functions.
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Public method to perform a GET request.
   * This method retrieves data from the specified path and optionally caches it.
   * It retries the request in case of certain server errors or network issues.
   * 
   * @param {string} path - The URL path for the GET request.
   * @param {Record<string, unknown>} [query] - Optional query parameters to customize the request.
   * @returns {Promise<ApiResponse<T>>} - A promise that resolves with the ApiResponse<T> type.
   * @template T - The expected type of the response data.
   */
  get<T>(path: string, query?: Record<string, unknown>): Promise<ApiResponse<T>> {
    return this.requestWithRetry<T>('GET', path, undefined, query);
  }

  /**
   * Public method to perform a POST request.
   * This method sends data to the specified path. It retries the request in case of
   * certain server errors or network issues.
   *
   * @param {string} path - The URL path for the POST request.
   * @param {unknown} body - The payload for the POST request.
   * @returns {Promise<ApiResponse<T>>} - A promise that resolves with the ApiResponse<T> type.
   * @template T - The expected type of the response data.
   */
  post<T>(path: string, body: unknown): Promise<ApiResponse<T>> {
    return this.requestWithRetry<T>('POST', path, body);
  }
}